import type { ReactElement } from 'react';
import { Trans, type UseTranslationResponse } from 'react-i18next';
import type { ApproachingPromotion } from '@/types/scapi';
import { cn } from '@/lib/utils';
import { SvgAlert, SvgRight, SvgShipping } from '@/components/icons-eataly';
import { formatCurrency } from '@/lib/currency';

export type CartTFunction = UseTranslationResponse<'cart', undefined>['t'];

export type ApproachingPromotionsBannerVariant = 'page' | 'toast' | 'drawer';

export type ApproachingPromotionsBannerProps = {
    /** Promotion progress items from `basket.c_approachingPromotions`. */
    promotions: ApproachingPromotion[];
    /** Site currency for formatting when `promotion.currency` is absent. */
    currency: string;
    /** Cart namespace translator — must be resolved by the caller (toast portals lack I18nextProvider). */
    t: CartTFunction;
    /** Active locale for `formatCurrency` (e.g. `i18n.language`). */
    language: string;
    className?: string;
    variant?: ApproachingPromotionsBannerVariant;
};

const AMOUNT_HIGHLIGHT_REGEX = /([^\w\s.,]+\s*\d+[.,]\d+)/;

export function BelowMinimumPromotionBanner({
    minThreshold,
    language,
    currency,
    t,
    isPage,
    isToast,
}: {
    minThreshold: number;
    language: string;
    currency: string;
    t: CartTFunction;
    isPage: boolean;
    isToast: boolean;
}): ReactElement {
    return (
        <div
            className={cn(
                'flex flex-col gap-2 ',
                isPage && 'p-4',
                isToast ? 'bg-neutral-white' : 'p-4 rounded-lg border border-neutral-80'
            )}>
            <div className="flex items-center gap-2">
                <SvgAlert className="size-6 shrink-0 text-status-red-40" aria-hidden="true" />
                <p className={cn(isToast ? 'body-copy-s-bold' : 'body-copy-m-bold', 'text-status-red-40')}>
                    {t('shippingProgress.belowMinimumTitle', {
                        amount: formatCurrency(minThreshold, language, currency),
                    })}
                </p>
            </div>

            {!isToast && <p className="body-copy-m">{t('shippingProgress.excludedProductsDisclaimer')}</p>}
        </div>
    );
}

function ApproachingPromotionBanner({
    promotion,
    currency,
    t,
    language,
    variant = 'page',
}: {
    promotion: ApproachingPromotion;
    currency: string;
    t: CartTFunction;
    language: string;
    variant?: ApproachingPromotionsBannerVariant;
}): ReactElement | null {
    const isToast = variant === 'toast';
    const isPage = variant === 'page';
    const isShipping = promotion.isShipping;
    const isDrawer = variant === 'drawer';

    if (promotion.type === 'achieved') {
        return (
            <div
                className={cn(
                    'flex items-center gap-3 body-copy-m ',
                    isPage && 'p-4',
                    isToast || isDrawer ? 'bg-neutral-white' : ' p-4 rounded-lg border border-neutral-80'
                )}>
                {isShipping ? (
                    <SvgShipping className="size-6 shrink-0 text-neutral-10" aria-hidden="true" />
                ) : (
                    <SvgRight className="size-6 shrink-0 text-neutral-10" aria-hidden="true" />
                )}
                <div>
                    <Trans
                        t={t}
                        i18nKey="shippingProgress.freeShippingReached"
                        components={{
                            bold: <span className={isToast ? 'body-copy-s-bold' : 'body-copy-m-bold'} />,
                        }}
                    />
                </div>
            </div>
        );
    }

    if (promotion.type === 'below_minimum' && promotion.isShipping) {
        const minThreshold = promotion.minShippingThreshold;
        if (typeof minThreshold !== 'number' || minThreshold <= 0) {
            return null;
        }

        return (
            <BelowMinimumPromotionBanner
                minThreshold={minThreshold}
                language={language}
                currency={currency}
                t={t}
                isPage={isPage}
                isToast={isToast || isDrawer}
            />
        );
    }

    if (promotion.type === 'approaching') {
        const message = promotion.message;
        if (!message) {
            return null;
        }

        const parts = message.split(AMOUNT_HIGHLIGHT_REGEX);
        const progressPercent = promotion.targetAmount
            ? Math.min(100, ((promotion.currentTotal ?? 0) / promotion.targetAmount) * 100)
            : 0;

        return (
            <div
                className={cn(
                    'flex flex-col gap-2',
                    isPage && 'p-4',
                    isToast || isDrawer ? 'bg-neutral-white' : ' rounded-lg border border-neutral-80'
                )}>
                <p className={isToast ? 'body-copy-s text-neutral-10' : 'body-copy-m'}>
                    {parts.map((part) => {
                        if (AMOUNT_HIGHLIGHT_REGEX.test(part)) {
                            return (
                                <span key={part} className={isToast ? 'body-copy-s-bold' : 'body-copy-m-bold'}>
                                    {part}
                                </span>
                            );
                        }
                        return part;
                    })}
                </p>
                {!isDrawer && (
                    <div className={cn('relative h-2 w-full rounded-full bg-sand-90 my-2', !isToast && 'my-3')}>
                        <div
                            className="absolute top-0 left-0 h-full rounded-full bg-brown-10 transition-all duration-300"
                            style={{ width: `${progressPercent}%` }}
                        />
                        <div
                            className={cn(
                                'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brown-10 text-neutral-white shadow transition-all duration-300',
                                isToast ? 'p-1' : 'p-1.5'
                            )}
                            style={{ left: `${progressPercent}%` }}>
                            {isShipping ? (
                                <SvgShipping
                                    className={cn(isToast ? 'size-5' : 'size-4', 'text-neutral-white')}
                                    aria-hidden="true"
                                />
                            ) : (
                                <SvgRight
                                    className={cn(isToast ? 'size-5' : 'size-4', 'text-neutral-white')}
                                    aria-hidden="true"
                                />
                            )}
                        </div>
                    </div>
                )}
                {isPage && promotion.isShipping && promotion.targetAmount != null && (
                    <p className="body-copy-s text-neutral-10">
                        {t('shippingProgress.excludedProductsDisclaimer2', {
                            amount: formatCurrency(promotion.targetAmount, language, currency, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                            }),
                        })}
                    </p>
                )}
            </div>
        );
    }
    return null;
}

/**
 * Renders approaching promotion banners returned by the basket SCAPI hook.
 */
export function ApproachingPromotionsBanner({
    promotions,
    currency,
    t,
    language,
    className,
    variant = 'page',
}: ApproachingPromotionsBannerProps): ReactElement | null {
    if (promotions.length === 0) {
        return null;
    }

    return (
        <div className={cn(variant === 'page' && 'flex w-full flex-col gap-2 lg:gap-3', className)}>
            {promotions.map((promotion) => (
                <ApproachingPromotionBanner
                    key={promotion.id}
                    promotion={promotion}
                    currency={currency}
                    t={t}
                    language={language}
                    variant={variant}
                />
            ))}
        </div>
    );
}
